import { useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { Loader2, Lock, CheckCircle } from "lucide-react";
import { LOGO_URL } from "../config";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

export default function ResetPasswordPage() {
  const { lang } = useLang();
  const ar = lang === "ar";
  const { token } = useParams();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError(ar ? "كلمة المرور يجب أن تكون ٨ أحرف على الأقل" : "Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      setError(ar ? "كلمتا المرور غير متطابقتين" : "Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/customer/reset-password`, { token, password: form.password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || (ar ? "حدث خطأ — حاولي مرة أخرى" : "Something went wrong — please try again"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-luxury-black flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-luxury-surface border border-luxury-border p-10">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="TheMaya" className="h-10 mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-luxury-bone">{ar ? "كلمة مرور جديدة" : "New Password"}</h1>
        </div>
        {done ? (
          <div className="text-center space-y-6" data-testid="reset-success">
            <CheckCircle className="w-12 h-12 mx-auto text-luxury-gold" strokeWidth={1.25} />
            <p className="text-luxury-bone">{ar ? "تم تحديث كلمة مرورك بنجاح" : "Your password has been updated"}</p>
            <Link to="/account" data-testid="reset-goto-login"
              className="inline-block bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors">
              {ar ? "تسجيل الدخول" : "Sign in"}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="reset-password-form">
            <div>
              <label className="block text-sm text-luxury-gray mb-2">{ar ? "كلمة المرور الجديدة" : "New password"}</label>
              <input data-testid="reset-password-input" type="password" dir="ltr" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} required minLength={8} />
            </div>
            <div>
              <label className="block text-sm text-luxury-gray mb-2">{ar ? "تأكيد كلمة المرور" : "Confirm password"}</label>
              <input data-testid="reset-confirm-input" type="password" dir="ltr" value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} required minLength={8} />
            </div>
            {error && <p data-testid="reset-error" className="text-red-400 text-sm text-center">{error}</p>}
            <button data-testid="reset-submit-button" type="submit" disabled={loading}
              className="w-full bg-luxury-gold text-luxury-black font-semibold py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" strokeWidth={1.5} />}
              {ar ? "حفظ كلمة المرور" : "Save password"}
            </button>
            <Link to="/account" data-testid="reset-back-login" className="block text-center text-luxury-gray text-sm hover:text-luxury-gold transition-colors">
              {ar ? "العودة لتسجيل الدخول" : "Back to sign in"}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
